import React, { useEffect, useState, useCallback, SyntheticEvent } from "react";
import { ResultPaginationNumberedProps } from "@usereactify/search";

export type PaginationNumberedProps = React.ComponentProps<NonNullable<ResultPaginationNumberedProps["render"]>>;

export const PaginationNumbered: React.FC<PaginationNumberedProps> = (props) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const handleWindowDimensionChange = useCallback(() => setIsMobile(window.innerWidth < 1024), [isMobile]);

  useEffect(() => {
    handleWindowDimensionChange();
    window.addEventListener("resize", handleWindowDimensionChange);
    return () => {
      window.removeEventListener("resize", handleWindowDimensionChange);
    }
  }, [isMobile]);

  const scrollToTop = () => {
    const targetDiv = document.querySelector("[data-search-grid]");
    if (targetDiv) {
      const divPosition = targetDiv.getBoundingClientRect().top + window.pageYOffset;
      const offset = 250;
      window.scrollTo({
        top: divPosition - offset,
        behavior: "smooth",
      });
    }
  };

  const handleNextPage = useCallback((e: SyntheticEvent<Element, Event>) => {
    props.handleNextPage(e);
    scrollToTop();
  }, [props?.handleNextPage]);

  const handlePrevPage = useCallback((e: SyntheticEvent<Element, Event>) => {
    props.handlePreviousPage(e);
    scrollToTop();
  }, [props?.handlePreviousPage]);

  const handlePageChange = useCallback((pageNumber: number, e?: React.SyntheticEvent<Element, Event> | undefined) => {
    props.handlePageChange(pageNumber, e);
    scrollToTop();
  }, [props?.handlePageChange]);

  return (
    <>
      {isMobile ? (
        <nav className="rs__pagination__nav mobile">
          {props.currentPage > 0 && (
            <a
              className="rs__pagination__prev-link"
              rel="prev"
              href={props.buildPagePath(props.currentPage)}
              onClick={handlePrevPage}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="800px"
                width="800px"
                version="1.1"
                id="Capa_1"
                viewBox="0 0 227.096 227.096"
              >
                <g>
                  <g>
                    <polygon points="152.835,39.285 146.933,45.183 211.113,109.373 0,109.373 0,117.723 211.124,117.723     146.933,181.902 152.835,187.811 227.096,113.55   " />
                  </g>
                </g>
              </svg>
            </a>
          )}

          <span className="rs__pagination__pagenumber-mobile">
            {`Page ${props.currentPage + 1}/${props.totalPages}`}
          </span>

          {props.currentPage + 1 < props.totalPages && (
            <a
              className="rs__pagination__next-link"
              rel="next"
              href={props.buildPagePath(props.currentPage + 2)}
              onClick={handleNextPage}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="800px"
                width="800px"
                version="1.1"
                id="Capa_1"
                viewBox="0 0 227.096 227.096"
              >
                <g>
                  <g>
                    <polygon points="152.835,39.285 146.933,45.183 211.113,109.373 0,109.373 0,117.723 211.124,117.723     146.933,181.902 152.835,187.811 227.096,113.55   " />
                  </g>
                </g>
              </svg>
            </a>
          )}
        </nav>
      ) : (
        <nav className="rs__pagination__nav">
          {props.currentPage > 0 && (
            <a
              className="rs__pagination__prev-link"
              rel="prev"
              href={props.buildPagePath(props.currentPage)}
              onClick={handlePrevPage}
            >
              <span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="800px"
                  width="800px"
                  version="1.1"
                  id="Capa_1"
                  viewBox="0 0 227.096 227.096"
                >
                  <g>
                    <g>
                      <polygon points="152.835,39.285 146.933,45.183 211.113,109.373 0,109.373 0,117.723 211.124,117.723     146.933,181.902 152.835,187.811 227.096,113.55   " />
                    </g>
                  </g>
                </svg>
              </span>
              <span>{" Prev"}</span>
            </a>
          )}
          {!props.pagesToShow.includes(0) && (
            <a
              className="rs__pagination__pagenumber-link"
              href={props.buildPagePath(1)}
              onClick={(event) => handlePageChange(0, event)}
              rel={0 === props.currentPage - 1 ? `prev` : undefined}
            >
              {"1"}
            </a>
          )}
          {!props.pagesToShow.includes(1) && <span>...</span>}
          {props.pagesToShow.map((page) =>
            page === props.currentPage ? (
              <span className="rs__pagination__pagenumber-active" key={page + 1}>
                {page + 1}
              </span>
            ) : (
              <a
                className="rs__pagination__pagenumber-link"
                key={page + 1}
                href={props.buildPagePath(page + 1)}
                onClick={(event) => handlePageChange(page, event)}
                rel={page === props.currentPage + 1 ? `next` : page === props.currentPage - 1 ? `prev` : undefined}
              >
                {page + 1}
              </a>
            )
          )}
          {!props.pagesToShow.includes(props.totalPages - 2) && <span>...</span>}
          {!props.pagesToShow.includes(props.totalPages - 1) && (
            <a
              className="rs__pagination__pagenumber-link"
              href={props.buildPagePath(props.totalPages)}
              onClick={(event) => handlePageChange(props.totalPages - 1, event)}
              rel={
                props.totalPages - 1 === props.currentPage + 1
                  ? `next`
                  : props.totalPages - 1 === props.currentPage - 1
                  ? `prev`
                  : undefined
              }
            >
              {props.totalPages}
            </a>
          )}
          {props.currentPage + 1 < props.totalPages && (
            <a
              className="rs__pagination__next-link"
              rel="next"
              href={props.buildPagePath(props.currentPage + 2)}
              onClick={handleNextPage}
            >
              <span>{"Next"}</span>
              <span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="800px"
                  width="800px"
                  version="1.1"
                  id="Capa_1"
                  viewBox="0 0 227.096 227.096"
                >
                  <g>
                    <g>
                      <polygon points="152.835,39.285 146.933,45.183 211.113,109.373 0,109.373 0,117.723 211.124,117.723     146.933,181.902 152.835,187.811 227.096,113.55   " />
                    </g>
                  </g>
                </svg>
              </span>
            </a>
          )}
        </nav>
      )}
    </>
  );
};
